import React from 'react'
import ReactMarkdown from 'react-markdown'
import clsx from 'clsx'
import rehypeRaw from 'rehype-raw'
import { useIntersectionObserver } from 'usehooks-ts'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_COLOR_NEUTRAL, BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import Takeover, { TakeoverActionBar, TakeoverBody } from '@divvy-web/skylab.takeover'
import Tooltip, { TOOLTIP_POSITION_TOP } from '@divvy-web/skylab.tooltip'

import styles from './styles.module.scss'

interface AgreementPopsheetProps {
  isAcceptingTilaAgreement: boolean
  isShowing: boolean
  hasAgreedToTila: boolean
  tilaContent: string
  onCancel: () => void
  onAgreeClick: () => void
}

const AgreementPopsheet = ({
  isAcceptingTilaAgreement,
  isShowing,
  hasAgreedToTila,
  tilaContent,
  onCancel,
  onAgreeClick,
}: AgreementPopsheetProps) => {
  const [getClassName, makeTestId] = useNameFormatter('AgreementPopsheet')
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 1,
  })

  return (
    <Takeover
      className={clsx(getClassName(''), 'fs-unmask')}
      dataTestId={makeTestId('')}
      isShowing={isShowing}
      onCloseComplete={onCancel}
    >
      <TakeoverBody>
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{tilaContent}</ReactMarkdown>
        <span
          ref={ref}
          className={styles[getClassName('ref-container')]}
          data-testid='ref-container'
        />
      </TakeoverBody>
      <TakeoverActionBar className={styles[getClassName('action-bar')]}>
        {!hasAgreedToTila && (
          <Tooltip
            dataTestId={makeTestId('scroll-to-bottom-tooltip')}
            disableTooltip={isIntersecting}
            html={
              <FormattedMessage
                defaultMessage='Scroll to the bottom of the terms document to enable the agree button.'
                id='sputnik.AgreementPopsheet__7zcKKH'
              />
            }
            position={TOOLTIP_POSITION_TOP}
          >
            <BasicButton
              className={styles[getClassName('popsheet-top-footer-button')]}
              dataTestId={makeTestId('popsheet-review-offer')}
              disabled={isAcceptingTilaAgreement || !isIntersecting}
              onClick={onAgreeClick}
            >
              <FormattedMessage
                defaultMessage='Agree'
                id='sputnik.AgreementPopsheet__ISly67'
              />
            </BasicButton>
          </Tooltip>
        )}
        <BasicButton
          className={styles[getClassName('popsheet-bottom-footer-button')]}
          color={BASIC_BUTTON_COLOR_NEUTRAL}
          dataTestId={makeTestId('dismiss-popsheet')}
          type={BASIC_BUTTON_TYPE_FLAT}
          onClick={onCancel}
        >
          <FormattedMessage
            defaultMessage='Cancel'
            id='sputnik.AgreementPopsheet__47FYwb'
          />
        </BasicButton>
      </TakeoverActionBar>
    </Takeover>
  )
}

export default AgreementPopsheet
